import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CallSplitOutlined,
  ContentCopyOutlined,
  DnsOutlined,
  ForwardToInboxOutlined,
  HistoryOutlined,
  PhoneForwardedOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";

const TIMEZONES = ["UTC", "America/Guayaquil", "America/Bogota", "America/Lima", "America/New_York", "America/Chicago"];

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
  },
  helper: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(3),
    alignItems: "start",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  groupTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(1),
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
  addressBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: "0.9rem",
  },
  warning: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.warning.main}`,
    color: theme.palette.warning.main,
    fontSize: "0.82rem",
  },
  fieldChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  stats: {
    display: "flex",
    gap: theme.spacing(3),
    fontSize: "0.86rem",
  },
  mono: {
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: "0.82rem",
  },
  empty: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: theme.spacing(1),
  },
}));

const splitValues = (values) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => String(value).split(","))
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );

const ChipsField = ({ label, helperText, placeholder, value, onChange, disabled }) => (
  <Autocomplete
    multiple
    disabled={disabled}
    freeSolo
    autoSelect
    options={[]}
    value={value}
    onChange={(e, values) => onChange(splitValues(values))}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        size="small"
        variant="outlined"
        helperText={helperText}
        placeholder={value.length ? "" : placeholder}
      />
    )}
  />
);

const formatTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "medium" });
};

const CallRecordsSettings = () => {
  const { classes } = useStyles();

  const [enabled, setEnabled] = useState(false);
  const [allowedIps, setAllowedIps] = useState([]);
  const [ignoredExtensions, setIgnoredExtensions] = useState([]);
  const [forwardTargets, setForwardTargets] = useState([]);
  const [timezone, setTimezone] = useState("UTC");
  const [listener, setListener] = useState({ host: "", port: null });
  const [fields, setFields] = useState([]);
  const [stats, setStats] = useState({ receivedToday: 0, matchedToday: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/call-records");
      setEnabled(Boolean(data.config?.enabled));
      setAllowedIps(data.config?.allowedIps || []);
      setIgnoredExtensions(data.config?.ignoredExtensions || []);
      setForwardTargets(data.config?.forwardTargets || []);
      setTimezone(data.config?.timezone || "UTC");
      setListener(data.listener || { host: "", port: null });
      setFields(data.fields || []);
      setStats(data.stats || { receivedToday: 0, matchedToday: 0 });
      setRecent(data.recent || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (saving) return;

    const invalidTarget = forwardTargets.find((target) => !/^[^:\s]+:\d{1,5}$/.test(target));
    if (invalidTarget) {
      toast.error(`"${invalidTarget}" no es válido. Use el formato IP:puerto, por ejemplo 10.0.0.5:8090.`);
      return;
    }

    setSaving(true);
    try {
      await api.put("/integrations/call-records", {
        enabled,
        allowedIps,
        ignoredExtensions,
        forwardTargets,
        timezone,
      });
      toast.success("Configuración guardada.");
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado.");
    } catch (err) {
      toast.error("No se pudo copiar.");
    }
  };

  const address = listener.port ? `${listener.host || "IP-de-este-servidor"}:${listener.port}` : "";

  return (
    <SettingsCard
      icon={CallSplitOutlined}
      title="Registro de llamadas de la central (CDR)"
      subtitle="La central telefónica envía un registro (CDR) al terminar cada llamada. Con eso se sabe qué extensión atendió al final una llamada que la IA transfirió."
    >
      <Box className={classes.root}>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} color="primary" />}
          label="Recibir registros de la central"
        />

        <div className={classes.columns}>
          <div className={classes.column}>
            <div className={classes.groupTitle}>
              <DnsOutlined fontSize="small" /> Dirección para cargar en la central
            </div>
            {address ? (
              <div className={classes.addressBox}>
                <span>{address}</span>
                <Tooltip title="Copiar">
                  <IconButton size="small" onClick={() => copy(address)}>
                    <ContentCopyOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            ) : (
              <Typography className={classes.warning}>
                El receptor no está activo en el servidor. Falta la variable de entorno CDR_PORT y publicar
                ese puerto en Docker.
              </Typography>
            )}
            <Typography className={classes.helper}>
              Configure en la central el envío del CDR a esta IP y puerto (por ejemplo, en 3CX: Admin → Configuración → CDR → modo "Active socket").
            </Typography>

            <div className={classes.groupTitle}>
              <HistoryOutlined fontSize="small" /> Campos a enviar, en este orden
            </div>
            <div className={classes.fieldChips}>
              {fields.map((field, index) => (
                <Chip key={field} size="small" variant="outlined" label={`${index + 1}. ${field}`} />
              ))}
            </div>
            <Box>
              <Button
                size="small"
                startIcon={<ContentCopyOutlined fontSize="small" />}
                disabled={!fields.length}
                onClick={() => copy(fields.join(", "))}
              >
                Copiar lista de campos
              </Button>
            </Box>
          </div>

          <div className={classes.column}>
            <div className={classes.groupTitle}>
              <PhoneForwardedOutlined fontSize="small" /> Llamadas
            </div>
            <ChipsField
              label="IP de la central (opcional)"
              helperText="Solo se aceptan registros que lleguen de estas IP. Vacío acepta cualquiera."
              placeholder="Ej.: 159.89.181.191 y Enter"
              value={allowedIps}
              onChange={setAllowedIps}
              disabled={!enabled}
            />
            <ChipsField
              label="Extensiones a ignorar"
              helperText="IVR, colas y extensiones que no son personas. No cuentan como la extensión que atendió."
              placeholder="Ej.: 8080 y Enter"
              value={ignoredExtensions}
              onChange={setIgnoredExtensions}
              disabled={!enabled}
            />
            <Autocomplete
              freeSolo
              options={TIMEZONES}
              disabled={!enabled}
              value={timezone}
              onChange={(e, value) => setTimezone(value || "UTC")}
              onInputChange={(e, value) => setTimezone(value || "UTC")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Zona horaria de la central"
                  size="small"
                  variant="outlined"
                  helperText="En qué zona horaria manda la central las horas de cada registro."
                />
              )}
            />

            <div className={classes.groupTitle}>
              <ForwardToInboxOutlined fontSize="small" /> Reenviar copia a
            </div>
            <ChipsField
              label="Destinos (IP:puerto)"
              placeholder="Ej.: 10.0.0.5:8090 y Enter"
              value={forwardTargets}
              onChange={setForwardTargets}
              disabled={!enabled}
            />

            <div className={classes.actions}>
              <Button variant="contained" color="primary" disabled={loading || saving} onClick={save}>
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </div>

        <div className={classes.groupTitle}>
          <HistoryOutlined fontSize="small" /> Últimos registros recibidos
        </div>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <div className={classes.stats}>
            <span>
              Recibidos hoy: <strong>{stats.receivedToday}</strong>
            </span>
            <span>
              Asociados a llamadas de la IA: <strong>{stats.matchedToday}</strong>
            </span>
          </div>
          <Tooltip title="Actualizar">
            <span>
              <IconButton size="small" disabled={loading} onClick={load}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {recent.length === 0 ? (
          <Typography className={classes.empty}>Todavía no llegó ningún registro.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Recibido</TableCell>
                <TableCell>Desde</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Atendió</TableCell>
                <TableCell align="center">Llamada IA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recent.map((record) => (
                <Tooltip key={record.id} title={record.rawLine} placement="top-start">
                  <TableRow hover>
                    <TableCell>{formatTime(record.createdAt)}</TableCell>
                    <TableCell className={classes.mono}>{record.sourceIp || "—"}</TableCell>
                    <TableCell className={classes.mono}>{record.fromNumber || "—"}</TableCell>
                    <TableCell>
                      {record.finalDn
                        ? `${record.finalDn}${record.finalDisplayName ? ` · ${record.finalDisplayName}` : ""}`
                        : "—"}
                    </TableCell>
                    <TableCell align="center">{record.avatarSessionId ? "Sí" : "—"}</TableCell>
                  </TableRow>
                </Tooltip>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </SettingsCard>
  );
};

export default CallRecordsSettings;
